import { useEffect, useMemo, useState } from "react";

// Material
import { Grid, Typography } from "@mui/material";

// Components
import SubmissionStatuses from "../../SubmissionStatuses";

// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { usePayPeriodsProviders } from "@/Washouts/hooks/PayPeriodsProviders/usePayPeriodsProviders";

import { getPayPeriodParts } from "@/Washouts/helpers/SplitPeriods";

// Constants
import actions from "@/Constants/actionsList";

// Types
import { IPayrollClosingSideBarSelected } from "@/Washouts/types/PayrollClosing/PayrollClosingSideBar";
import CommissionCyclePayrollDialog from "./CommissionCyclePayrollDialog";
import { ICommissionCycle, IPayPeriod, IPayPeriodProviders, IStatus, TPayPeriodPart } from "@/Common/api/types/CommissionCycle";
import { resolveSelectedPositionsDefault } from "@/Washouts/helpers/PayrollClosing/resolveSelectedPositionsDefault";
import { combinePayPeriodsPositionStatus } from "@/Washouts/helpers/PayrollClosing/combinePayPeriodsPositionStatus";

type CommissionCyclePayrollProps = {
	selectedItem: IPayrollClosingSideBarSelected | null;
	selection: boolean;
	locationId?: string;
	positionStatus: IStatus[];
	commissionCycle?: ICommissionCycle;
	payPeriod?: IPayPeriod;
	partnerPayPeriod?: IPayPeriod | null;
	positionStatusPayPeriodParts?: IStatus[];
};

const allowedStatuses = ["APPROVED", "POSTED_PAYROLL"];

const buildPayPeriodsList = (selectedItem: IPayrollClosingSideBarSelected | null) => {
	if (!selectedItem?.payPeriodProviders?.length) {
		return [];
	}
	return selectedItem.payPeriodProviders.map((payPeriodProvider) => ({
		providerPayPeriodId: payPeriodProvider.providerPayPeriodId,
		status: payPeriodProvider.status,
		companyId: payPeriodProvider.companyId,
		displayId: payPeriodProvider.displayId,
		combinedDisplay: `${payPeriodProvider.providerPayPeriodId} (CID: ${payPeriodProvider.displayId ?? "none"})`,
	}));
};

const concatCCIds = (payPeriodParts: TPayPeriodPart[]) => {
	return payPeriodParts
		.map((part) => part.ccId)
		.sort((a = "", b = "") => a?.localeCompare(b))
		.join("-");
};

export default function CommissionCyclePayroll(props: Readonly<CommissionCyclePayrollProps>) {
	const { selection, commissionCycle, payPeriod, partnerPayPeriod, selectedItem, locationId = "" } = props;

	const { useAuth } = useAuthorization();
	const commissionCycleId = commissionCycle?._id ?? "";
	const editPayroll = useAuth(actions.SUBMIT_PAYROLL);
	const isFinalized = commissionCycle?.status === "FINALIZED";
	const isCompletedPP = payPeriod?.status === "COMPLETED";
	const payPeriodParts = useMemo(
		() => getPayPeriodParts({ commissionCycle, payPeriod, partnerPayPeriod }),
		[commissionCycle, payPeriod, partnerPayPeriod]
	);

	const [payPeriodsProviders, setPayPeriodsProviders] = useState<IPayPeriodProviders[]>([]);
	const [selectedPayPeriodPart, setSelectedPayPeriodPart] = useState<string>(payPeriodParts?.[0]?.ccId ?? commissionCycleId);
	const [selectedPositions, setSelectedPositions] = useState<Record<string, string[]>>(
		resolveSelectedPositionsDefault(commissionCycleId, payPeriod, partnerPayPeriod)
	);

	const amountOfSelectedPositions = Object.values(selectedPositions).reduce((acc, positions) => acc + positions.length, 0);

	usePayPeriodsProviders({
		locationId,
		payPeriodId: payPeriod?._id ?? "",
		onExecutedSubscription: (response: unknown) => {
			const res = response as { payPeriods: IPayrollClosingSideBarSelected[] };
			const { payPeriods = [] } = res ?? {};
			const receivedPayPeriod = payPeriods.find((period: IPayrollClosingSideBarSelected) => period._id === selectedItem?.payPeriodId);
			if (!receivedPayPeriod) {
				return;
			}
			setPayPeriodsProviders(buildPayPeriodsList(receivedPayPeriod));
		},
	});

	const clearSelectedPositions = () => {
		setSelectedPositions((prev) =>
			Object.keys(prev).reduce(
				(acc, key) => {
					acc[key] = [];
					return acc;
				},
				{} as Record<string, string[]>
			)
		);
	};

	useEffect(() => {
		setPayPeriodsProviders(buildPayPeriodsList(selectedItem));
		setSelectedPayPeriodPart(payPeriodParts?.[0]?.ccId ?? commissionCycleId);
		setSelectedPositions(resolveSelectedPositionsDefault(commissionCycleId, payPeriod, partnerPayPeriod));
		return () => {
			setPayPeriodsProviders([]);
			setSelectedPayPeriodPart("");
			clearSelectedPositions();
		};
	}, [commissionCycleId, selectedItem?.payPeriodId, concatCCIds(payPeriodParts)]);

	const combinedPositionStatuses = combinePayPeriodsPositionStatus(payPeriod, partnerPayPeriod, commissionCycleId);

	return (
		<>
			{selection && (
				<Grid container>
					<Grid item xs={12}>
						<Typography className="page-title" component="div">
							Payroll Submission
						</Typography>
					</Grid>
					<Grid item xs={12} sx={{ paddingTop: "30px" }}>
						<SubmissionStatuses
							editPayroll={editPayroll}
							isCompletedPP={isCompletedPP}
							isFinalizedCC={isFinalized}
							partnerPayPeriod={partnerPayPeriod}
							allowedStatuses={allowedStatuses}
							payPeriodParts={payPeriodParts}
							selectedPayPeriodPart={selectedPayPeriodPart}
							selectedPositions={selectedPositions}
							setSelectedPayPeriodPart={setSelectedPayPeriodPart}
							setSelectedPositions={setSelectedPositions}
							combinedPositionStatuses={combinedPositionStatuses}
						/>
					</Grid>
					<Grid container item xs={12} justifyContent={"flex-end"}>
						<CommissionCyclePayrollDialog
							amountOfSelectedPositions={amountOfSelectedPositions}
							selectedPositions={selectedPositions}
							locationId={locationId}
							commissionCycleId={selectedItem?.cycleId}
							payPeriodId={selectedItem?.payPeriodId}
							combinedPositionStatuses={combinedPositionStatuses}
							payPeriodsProviders={payPeriodsProviders}
							clearSelectedPositions={clearSelectedPositions}
						/>
					</Grid>
				</Grid>
			)}
		</>
	);
}
